import type { ImageObject } from 'schema-dts';

// Test pages with their structured data requirements
// Source: DEV-2864 Linear ticket comments
export const IMAGE_STRUCTURED_DATA_TEST_PAGES = {
  'sunny-beats': {
    name: 'Sunny Beats',
    altText:
      "A pair of blue sunglasses on a yellow background to fit the vibe of Uppbeat's Sunny Beats playlist of copyright-free music.",
  },
  'lo-fi-beats': {
    name: 'Lofi Beats',
    altText:
      "Image of a cassette tape on a blue background to visualise the aesthetic of Uppbeat's Lofi Beats playlist of copyright-free music.",
  },
  inspiring: {
    name: 'Inspiring Music',
    altText:
      "Image of day breaking over the earth to complement the mood of Uppbeat's Inspiring Music playlist of copyright-free tracks.",
  },
  energizing: {
    name: 'Energizing Music',
    altText:
      "Photo of a snowboarder in mid-air to match the vibe of Uppbeat's Energizing Music playlist of copyright-free tracks.",
  },
  hype: {
    name: 'Hype Music',
    altText:
      "Image of a DJ with their arms raised to symbolise the vibe of Uppbeat's Hype Music playlist of copyright-free tracks.",
  },
  rock: {
    name: 'Rock Music',
    altText:
      "Photo of a guitarist in a rock band to illustrate Uppbeat's Rock Music playlist of copyright-free tracks.",
  },
  travel: {
    name: 'Travel Music',
    altText:
      "An orange campervan to signify the type of content that would suit Uppbeat's Travel Music playlist of copyright-free tracks.",
  },
  technology: {
    name: 'Technology Music',
    altText:
      "A woman wearing a VR headset to symbolize the type of creator that could use Uppbeat's Technology Music playlist of copyright-free tracks.",
  },
  'fast-cut': {
    name: 'Music For Fast-Cut Videos',
    altText:
      "Photo of a man running to show the energy of content that would work with Uppbeat's Music For Fast-Cut Videos playlist of copyright-free tracks.",
  },
  empowering: {
    name: 'Empowering Music',
    altText:
      "Image of a raised fist against an orange background to accompany Uppbeat's Empowering Music playlist of copyright-free tracks.",
  },
} as const;

export type TestPageSlug = keyof typeof IMAGE_STRUCTURED_DATA_TEST_PAGES;

/**
 * Extracts width and height from CDN URL parameters
 * Supports both BunnyCDN (width/height) and legacy Imgix (w/h) parameter names
 * @param imageUrl - The image URL with potential CDN parameters
 * @returns Object with width and height numbers, defaults to 384x384 if not found
 */
function extractImageDimensions(imageUrl: string): {
  width: number;
  height: number;
} {
  try {
    const url = new URL(imageUrl);
    // Support both BunnyCDN (width/height) and legacy Imgix (w/h) parameters
    const width = url.searchParams.get('width') ?? url.searchParams.get('w');
    const height = url.searchParams.get('height') ?? url.searchParams.get('h');

    return {
      width: width ? Number.parseInt(width, 10) : 384,
      height: height ? Number.parseInt(height, 10) : 384,
    };
  } catch {
    return { width: 384, height: 384 };
  }
}

/**
 * Checks if the current page is one of the test pages for image structured data
 * @param slug - The page slug to check
 * @returns boolean indicating if this is a test page
 */
export function isImageStructuredDataTestPage(
  slug: string
): slug is TestPageSlug {
  return slug in IMAGE_STRUCTURED_DATA_TEST_PAGES;
}

/**
 * Generates ImageObject structured data for category page images
 * Only applies to the test pages specified in DEV-2864
 * @param pageSlug - The category page slug
 * @param imageUrl - The URL of the category image
 * @returns ImageObject schema or null if not a test page
 */
export function getImageSchema(
  pageSlug: string,
  imageUrl: string
): ImageObject | null {
  if (!(isImageStructuredDataTestPage(pageSlug) && imageUrl)) {
    return null;
  }

  const testPageData = IMAGE_STRUCTURED_DATA_TEST_PAGES[pageSlug];
  const { width, height } = extractImageDimensions(imageUrl);

  return {
    '@type': 'ImageObject',
    url: imageUrl,
    name: testPageData.name,
    license: 'https://creativecommons.org/licenses/by/4.0/',
    width: width.toString(),
    height: height.toString(),
    caption: testPageData.altText,
  };
}
